/**
 * Race Result Modal Component
 * 
 * Modal dialog for creating/editing race results
 * - Form with rider, position, total_racers, lap times, total time, notes
 * - Form validation
 * - API integration
 * - Success/error handling
 * 
 * Requirements: 2.1-2.8, 4.1-4.5, 9.1, 9.2, 10.1-10.7
 */

import { useState, useCallback, useEffect } from 'react';
import { restClient, Rider } from '../../../api/rest-client';
import { RaceResult, RaceResultFormData } from '../../../types/raceResults';
import { parseTimeString, formatTimeForInput } from '../../../utils/raceResults';
import { Button } from '../../common/Button';
import { useUIStore } from '../../../store/uiStore';
import { formatRiderName } from '../../../utils/riderDisplay';

interface RaceResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  eventName?: string;
  onSuccess: () => void;
  editMode?: boolean;
  existingResult?: RaceResult;
}

interface ValidationErrors {
  rider_id?: string;
  position?: string;
  total_racers?: string;
  lap_time_1?: string;
  lap_time_2?: string;
  lap_time_3?: string;
  lap_time_4?: string;
  total_time?: string;
  notes?: string;
}

export function RaceResultModal({
  isOpen,
  onClose,
  eventId,
  eventName,
  onSuccess,
  editMode = false,
  existingResult
}: RaceResultModalProps) {
  const showToast = useUIStore(state => state.showToast);
  const [formData, setFormData] = useState<RaceResultFormData>({
    event_id: eventId,
    rider_id: '',
    position: 1,
    total_racers: undefined,
    category: '',
    lap_time_1: '',
    lap_time_2: '',
    lap_time_3: '',
    lap_time_4: '',
    total_time: '',
    dnf: false,
    notes: ''
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [riders, setRiders] = useState<Rider[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingRiders, setLoadingRiders] = useState(true);

  /**
   * Fetch riders for dropdown
   */
  const fetchRiders = useCallback(async () => {
    try {
      setLoadingRiders(true);
      const ridersList = await restClient.getRiders();
      console.log('[RaceResultModal] Fetched riders:', ridersList.length);
      console.log('[RaceResultModal] Sample rider:', ridersList[0]);
      // Filter to active riders only, excluding coaches
      const activeRiders = ridersList.filter(r => {
        const isActive = r.status === 'active' || !r.status;
        const isNotCoach = r.type !== 'Coach';
        return isActive && isNotCoach;
      });
      // Sort by name
      activeRiders.sort((a, b) => a.name.localeCompare(b.name));
      console.log('[RaceResultModal] Active riders:', activeRiders.length);
      console.log('[RaceResultModal] Riders with categories:', activeRiders.filter(r => r.category).length);
      setRiders(activeRiders);
    } catch (err) {
      console.error('Failed to fetch riders:', err);
      showToast('error', 'Failed to load riders list');
    } finally {
      setLoadingRiders(false);
    }
  }, [showToast]);

  /**
   * Reset form when modal opens/closes
   */
  useEffect(() => {
    if (isOpen) {
      if (editMode && existingResult) {
        // Pre-fill form with existing result data
        setFormData({
          event_id: eventId,
          rider_id: existingResult.rider_id,
          position: existingResult.position,
          total_racers: existingResult.total_racers,
          category: existingResult.category || '',
          lap_time_1: formatTimeForInput(existingResult.lap_time_1),
          lap_time_2: formatTimeForInput(existingResult.lap_time_2),
          lap_time_3: formatTimeForInput(existingResult.lap_time_3),
          lap_time_4: formatTimeForInput(existingResult.lap_time_4),
          total_time: formatTimeForInput(existingResult.total_time),
          dnf: existingResult.dnf || false,
          notes: existingResult.notes || ''
        });
      } else {
        // Reset form for new entry
        setFormData({
          event_id: eventId,
          rider_id: '',
          position: undefined as any, // Start empty so user can type freely
          total_racers: undefined,
          category: '',
          lap_time_1: '',
          lap_time_2: '',
          lap_time_3: '',
          lap_time_4: '',
          total_time: '',
          dnf: false,
          notes: ''
        });
      }
      setErrors({});
      fetchRiders();
    }
  }, [isOpen, eventId, editMode, existingResult, fetchRiders]);

  /**
   * Auto-populate category when riders list is loaded and rider is already selected
   * This handles the case where the modal opens with a pre-selected rider
   */
  useEffect(() => {
    console.log('[RaceResultModal] useEffect - riders loaded:', riders.length, 'rider_id:', formData.rider_id, 'category:', formData.category, 'editMode:', editMode);
    if (riders.length > 0 && formData.rider_id && !editMode) {
      const selectedRider = riders.find(r => r.rider_id === formData.rider_id);
      console.log('[RaceResultModal] Found rider in useEffect:', selectedRider, 'category:', selectedRider?.category);
      if (selectedRider && selectedRider.category && !formData.category) {
        console.log('[RaceResultModal] Auto-populating category in useEffect:', selectedRider.category);
        setFormData(prev => ({
          ...prev,
          category: selectedRider.category
        }));
      }
    }
  }, [riders, formData.rider_id, formData.category, editMode]);

  /**
   * Handle form field changes
   */
  const handleChange = useCallback((
    field: keyof RaceResultFormData,
    value: string | number | boolean | undefined
  ) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-populate category when rider is selected
      if (field === 'rider_id' && value) {
        const selectedRider = riders.find(r => r.rider_id === value);
        console.log('[RaceResultModal] Rider selected:', value, 'Found rider:', selectedRider);
        console.log('[RaceResultModal] Rider category:', selectedRider?.category);
        if (selectedRider && selectedRider.category) {
          newData.category = selectedRider.category;
          console.log('[RaceResultModal] Auto-populated category:', selectedRider.category);
        } else {
          console.log('[RaceResultModal] No category to populate - rider:', selectedRider, 'has category:', selectedRider?.category);
        }
      }
      
      // Clear time fields when DNF is checked
      if (field === 'dnf' && value === true) {
        newData.lap_time_1 = '';
        newData.lap_time_2 = '';
        newData.lap_time_3 = '';
        newData.lap_time_4 = '';
        newData.total_time = '';
      }
      
      return newData;
    });
    
    // Clear error for this field
    if (errors[field as keyof ValidationErrors]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field as keyof ValidationErrors];
        return newErrors;
      });
    }
  }, [errors, riders]);

  /**
   * Validate form
   */
  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    // Rider is required
    if (!formData.rider_id) {
      newErrors.rider_id = 'Please select a rider';
    }

    // Position is required and must be positive
    if (!formData.position || formData.position < 1) {
      newErrors.position = 'Position is required and must be a positive number';
    }

    // If total_racers is provided, position must be <= total_racers
    if (formData.position && formData.total_racers && formData.position > formData.total_racers) {
      newErrors.position = 'Position cannot be greater than total racers';
    }

    // Validate total time format (if provided and not DNF)
    if (!formData.dnf && formData.total_time && formData.total_time.trim() !== '') {
      const totalSeconds = parseTimeString(formData.total_time);
      if (totalSeconds === undefined) {
        newErrors.total_time = 'Invalid time format. Use HH:MM:SS or MM:SS';
      }
    }

    // Validate lap times format (if provided)
    if (formData.lap_time_1 && formData.lap_time_1.trim() !== '') {
      const lap1Seconds = parseTimeString(formData.lap_time_1);
      if (lap1Seconds === undefined) {
        newErrors.lap_time_1 = 'Invalid time format. Use HH:MM:SS or MM:SS';
      }
    }
    if (formData.lap_time_2 && formData.lap_time_2.trim() !== '') {
      const lap2Seconds = parseTimeString(formData.lap_time_2);
      if (lap2Seconds === undefined) {
        newErrors.lap_time_2 = 'Invalid time format. Use HH:MM:SS or MM:SS';
      }
    }
    if (formData.lap_time_3 && formData.lap_time_3.trim() !== '') {
      const lap3Seconds = parseTimeString(formData.lap_time_3);
      if (lap3Seconds === undefined) {
        newErrors.lap_time_3 = 'Invalid time format. Use HH:MM:SS or MM:SS';
      }
    }
    if (formData.lap_time_4 && formData.lap_time_4.trim() !== '') {
      const lap4Seconds = parseTimeString(formData.lap_time_4);
      if (lap4Seconds === undefined) {
        newErrors.lap_time_4 = 'Invalid time format. Use HH:MM:SS or MM:SS';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      showToast('error', 'Please fix the errors in the form');
      return;
    }

    try {
      setLoading(true);

      // Convert lap times to seconds (only if not DNF)
      const lap_time_1 = formData.dnf ? undefined : parseTimeString(formData.lap_time_1);
      const lap_time_2 = formData.dnf ? undefined : parseTimeString(formData.lap_time_2);
      const lap_time_3 = formData.dnf ? undefined : parseTimeString(formData.lap_time_3);
      const lap_time_4 = formData.dnf ? undefined : parseTimeString(formData.lap_time_4);
      const total_time = formData.dnf ? undefined : parseTimeString(formData.total_time);

      const submitData = {
        event_id: eventId,
        rider_id: formData.rider_id,
        position: formData.position!, // Non-null assertion - validated above
        total_racers: formData.total_racers,
        category: formData.category && formData.category.trim() !== '' ? formData.category : undefined,
        lap_time_1,
        lap_time_2,
        lap_time_3,
        lap_time_4,
        total_time,
        dnf: formData.dnf ? true : undefined,
        notes: formData.notes || undefined
      };

      console.log('Submitting race result with DNF:', formData.dnf, 'submitData:', submitData);

      if (editMode && existingResult) {
        // Update existing race result
        await restClient.updateRaceResult(submitData);
        showToast('success', 'Race result updated successfully');
      } else {
        // Create new race result
        await restClient.createRaceResult(submitData);
        showToast('success', 'Race result recorded successfully');
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to save race result:', err);
      showToast('error', `Failed to ${editMode ? 'update' : 'record'} race result. Please try again.`);
    } finally {
      setLoading(false);
    }
  }, [formData, eventId, editMode, existingResult, onSuccess, onClose, showToast]);

  /**
   * Handle cancel
   */
  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCancel}></div>

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900" id="modal-title">
                {editMode ? 'Edit Race Result' : 'Record Race Result'}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
                aria-label="Close"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {eventName && (
              <p className="text-sm text-gray-600 mt-1">Event: {eventName}</p>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
            {/* Rider Selection */}
            <div>
              <label htmlFor="rider_id" className="block text-sm font-medium text-gray-700 mb-1">
                Rider <span className="text-red-500">*</span>
              </label>
              {loadingRiders ? (
                <p className="text-sm text-gray-500">Loading riders...</p>
              ) : (
                <select
                  id="rider_id"
                  value={formData.rider_id}
                  onChange={(e) => handleChange('rider_id', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.rider_id ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={loading || editMode}
                >
                  <option value="">Select Rider...</option>
                  {riders.map((rider) => (
                    <option key={rider.rider_id} value={rider.rider_id}>
                      {formatRiderName(rider)}
                    </option>
                  ))}
                </select>
              )}
              {errors.rider_id && (
                <p className="mt-1 text-sm text-red-600">{errors.rider_id}</p>
              )}
            </div>

            {/* Category */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                Category (optional)
              </label>
              <select
                id="category"
                value={formData.category || ''}
                onChange={(e) => handleChange('category', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                disabled={loading}
              >
                <option value="">-- Select Category --</option>
                <option value="6th Grade">6th Grade</option>
                <option value="7th Grade">7th Grade</option>
                <option value="8th Grade">8th Grade</option>
                <option value="Freshman">Freshman</option>
                <option value="JV2">JV2</option>
                <option value="JV1">JV1</option>
                <option value="Varsity">Varsity</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Auto-filled from rider profile, but can be changed if needed
              </p>
            </div>

            {/* Position and Total Racers */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-1">
                  Position <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="position"
                  min="1"
                  value={formData.position || ''}
                  onChange={(e) => handleChange('position', e.target.value ? parseInt(e.target.value) : undefined)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.position ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={loading}
                  placeholder="e.g., 1"
                />
                {errors.position && (
                  <p className="mt-1 text-sm text-red-600">{errors.position}</p>
                )}
              </div>

              <div>
                <label htmlFor="total_racers" className="block text-sm font-medium text-gray-700 mb-1">
                  Total Racers (optional)
                </label>
                <input
                  type="number"
                  id="total_racers"
                  min="1"
                  value={formData.total_racers || ''}
                  onChange={(e) => handleChange('total_racers', e.target.value ? parseInt(e.target.value) : undefined)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  disabled={loading}
                  placeholder="e.g., 25"
                />
              </div>
            </div>

            {/* Lap Times */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lap Times (optional)
              </label>
              <div className="space-y-2">
                {/* Lap 1 */}
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600 w-12">Lap 1:</span>
                  <input
                    type="text"
                    placeholder="MM:SS or HH:MM:SS"
                    value={formData.lap_time_1 || ''}
                    onChange={(e) => handleChange('lap_time_1', e.target.value)}
                    className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.lap_time_1 ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={loading || formData.dnf}
                  />
                </div>
                {errors.lap_time_1 && (
                  <p className="text-sm text-red-600 ml-14">{errors.lap_time_1}</p>
                )}

                {/* Lap 2 */}
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600 w-12">Lap 2:</span>
                  <input
                    type="text"
                    placeholder="MM:SS or HH:MM:SS"
                    value={formData.lap_time_2 || ''}
                    onChange={(e) => handleChange('lap_time_2', e.target.value)}
                    className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.lap_time_2 ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={loading || formData.dnf}
                  />
                </div>
                {errors.lap_time_2 && (
                  <p className="text-sm text-red-600 ml-14">{errors.lap_time_2}</p>
                )}

                {/* Lap 3 */}
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600 w-12">Lap 3:</span>
                  <input
                    type="text"
                    placeholder="MM:SS or HH:MM:SS"
                    value={formData.lap_time_3 || ''}
                    onChange={(e) => handleChange('lap_time_3', e.target.value)}
                    className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.lap_time_3 ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={loading || formData.dnf}
                  />
                </div>
                {errors.lap_time_3 && (
                  <p className="text-sm text-red-600 ml-14">{errors.lap_time_3}</p>
                )}

                {/* Lap 4 */}
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600 w-12">Lap 4:</span>
                  <input
                    type="text"
                    placeholder="MM:SS or HH:MM:SS"
                    value={formData.lap_time_4 || ''}
                    onChange={(e) => handleChange('lap_time_4', e.target.value)}
                    className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.lap_time_4 ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={loading || formData.dnf}
                  />
                </div>
                {errors.lap_time_4 && (
                  <p className="text-sm text-red-600 ml-14">{errors.lap_time_4}</p>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {formData.dnf 
                  ? 'Lap times disabled when DNF is checked' 
                  : 'Format: MM:SS or HH:MM:SS (e.g., 2:30 or 1:15:45)'}
              </p>
            </div>

            {/* Total Time */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Total Time
                </label>
                <label className="flex items-center text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.dnf || false}
                    onChange={(e) => handleChange('dnf', e.target.checked)}
                    className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    disabled={loading}
                  />
                  DNF (Did Not Finish)
                </label>
              </div>
              <input
                type="text"
                placeholder="MM:SS or HH:MM:SS"
                value={formData.total_time || ''}
                onChange={(e) => handleChange('total_time', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.total_time ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={loading || formData.dnf}
              />
              {errors.total_time && (
                <p className="mt-1 text-sm text-red-600">{errors.total_time}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                {formData.dnf 
                  ? 'Time fields disabled when DNF is checked' 
                  : 'Format: MM:SS or HH:MM:SS (e.g., 2:30 or 1:15:45)'}
              </p>
            </div>

            {/* Notes */}
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                Notes (optional)
              </label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., Mechanical issue on lap 2"
                rows={3}
                maxLength={500}
                disabled={loading}
              />
              <p className="mt-1 text-xs text-gray-500">
                {formData.notes?.length || 0}/500 characters
              </p>
            </div>
          </form>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 sticky bottom-0 bg-white">
            <Button
              onClick={handleCancel}
              variant="secondary"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading || loadingRiders}
            >
              {loading ? (editMode ? 'Updating...' : 'Recording...') : (editMode ? 'Update Result' : 'Record Result')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

